import React from 'react';
import DiscoverIcon from '@atlaskit/icon/core/compass';
import EditorAddIcon from '@atlaskit/icon/core/add';
import EmojiFrequentIcon from '@atlaskit/icon/core/clock';
import { MenuGroup, LinkItem } from '@atlaskit/menu';
import AutomationIcon from '@atlassian/jira-automation-menu/src/common/assets/automation.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectProps } from '../../../common/types.tsx';
import {
	buildDiscoverAutomationUrl,
	buildAuditLogAutomationUrl,
	buildManageRulesAutomationUrl,
	buildNewRuleAutomationUrl,
} from '../../../common/utils/automation-urls.tsx';
import messages from './messages.tsx';

const automationIcon = <AutomationIcon label="" />;

type Props = ProjectProps & {
	includeDiscoverAutomation: boolean;
};

export const AutomationMenuDeepLinks = ({
	isSimplifiedProject,
	projectKey,
	projectType,
	includeDiscoverAutomation,
}: Props): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});
	const { formatMessage } = useIntl();
	return (
		// eslint-disable-next-line jira/integration/test-id-by-folder-structure
		<div data-testid="jira-releases-automation-menu.ui.automation-menu-content">
			<MenuGroup>
				{includeDiscoverAutomation ? (
					<LinkItem
						href={buildDiscoverAutomationUrl({
							projectKey,
							projectType,
							isSimplified: isSimplifiedProject,
						})}
						iconBefore={<DiscoverIcon spacing="spacious" label="" />}
						onClick={() => fireUIAnalytics(analyticsEvent, 'discoverAutomationLink')}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(messages.discoverAutomationMenuItemLabel)}
					</LinkItem>
				) : null}
				<LinkItem
					href={buildManageRulesAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={automationIcon}
					onClick={() => fireUIAnalytics(analyticsEvent, 'manageRulesAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.manageAutomationMenuItemLabel)}
				</LinkItem>
				<LinkItem
					href={buildNewRuleAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={<EditorAddIcon spacing="spacious" label="" />}
					onClick={() => fireUIAnalytics(analyticsEvent, 'newRuleAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.createAutomationMenuItemLabel)}
				</LinkItem>
				<LinkItem
					href={buildAuditLogAutomationUrl({
						projectKey,
						projectType,
						isSimplified: isSimplifiedProject,
					})}
					iconBefore={<EmojiFrequentIcon spacing="spacious" label="" />}
					onClick={() => fireUIAnalytics(analyticsEvent, 'auditLogAutomationLink')}
					UNSAFE_shouldDisableRouterLink
				>
					{formatMessage(messages.auditLogAutomationMenuItemLabel)}
				</LinkItem>
			</MenuGroup>
		</div>
	);
};
